import React, { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate, Routes, Route, useLocation, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { useAuthenticationStatus, useGetCurrentUser, useIsUserReady } from '@front/auth/src/authentication-context';

import { toRem } from '@front/common/src/utils/styles';
import { isChildcareRecurring as isChildcareRecurringUtil } from '@front/common/src/utils/basket';
import { getBasketMode } from '@front/common/src/ducks/customer/basket';
import { getIsInstantTaxCreditSelected } from '@front/common/src/ducks/customer/basket-selectors';

import { DESKTOP_GRID_WIDTH, LEFT_CONTAINER_MD_WIDTH } from '@front/web-ui/src/constants/layout';
import { minWidth } from '@front/web-ui/src/styles/mixins';
import { DesktopAndTablet } from '@front/web-ui/src/styles/media-queries';

import Basket from '../basket/basket';
import FormContainer from '../form-container/form-container';
import { shouldHideBasket, route } from '../../steps/steps';
import { getNextPath, getPreviousPath } from '../../utils/navigation';
import { trackScreenView, trackSignInEvent } from '../../utils/tracking';
import { RootState } from '../../store';
import { useIsLeavingApp } from '../../contexts/app-context';

import Header from './header';
import BasketContext from './basket-context';
import { isLoginOrSignup } from './utils';

// Components
const Container = styled.section`
  width: 100%;
  box-sizing: border-box;

  ${minWidth.md`
    padding: 20px;
    margin: 0 auto;
  `};

  ${minWidth.xl`
    width: ${toRem(DESKTOP_GRID_WIDTH)};
    padding: 20px 0;
  `};
`;

const Row = styled.div`
  display: flex;
`;

const Main = styled.main<{ $centered: boolean }>`
  width: 100%;
  margin: ${({ $centered }) => ($centered ? '0 auto' : '0')};

  ${minWidth.md`
    width: ${LEFT_CONTAINER_MD_WIDTH};
  `};
`;

// To be 100px of margin on a container of 1140px
// To be 480px of basket on a container of 1140px
const BasketContainer = styled.aside`
  width: 42.1%;
  display: flex;
  margin-left: 8.77%;
`;

const MainContainer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const status = useAuthenticationStatus();
  const isUserReady = useIsUserReady();
  const user = useGetCurrentUser();
  const { isLeavingApp } = useIsLeavingApp();

  const [searchParams] = useSearchParams();
  // Convert string | null to string | undefined to avoid modifying all trackScreenView occurrences
  const utmMedium = searchParams.get('utm_medium') || undefined;

  const universe = useSelector((state: RootState) => state.basket.universe);
  const isChildcareRecurring = useSelector((state: RootState) =>
    isChildcareRecurringUtil(state.basket.basketContent),
  );
  const mode = useSelector(getBasketMode);
  const cleaningPrestation = useSelector((state: RootState) => state.basket.cleaningPrestation);
  const isInstantTaxCreditSelected = useSelector(getIsInstantTaxCreditSelected);

  /*
   * If the user go to the login screen during his reservation we want to keep showing on the
   * breadcrumb the current step of the user. We use the from path we have in the location state
   */
  const stepPath = isLoginOrSignup(location.pathname)
    ? getNextPath(location) || getPreviousPath(location)
    : location.pathname;

  // Do not show basket at the end of tunnel when leaving app with loading or if explicitly hidden.
  const showBasket = useMemo(
    () => !shouldHideBasket(universe, stepPath) && !isLeavingApp && !isChildcareRecurring,
    [universe, stepPath, isLeavingApp, isChildcareRecurring],
  );

  const openLoginScreen = useCallback(() => {
    navigate(route('sign_in'), { state: { from: location.pathname, to: getNextPath(location) } });
  }, [navigate, location]);

  useEffect(() => {
    if (isUserReady && !isLeavingApp) {
      trackScreenView({
        cleaningPrestation,
        mode,
        path: location.pathname,
        taxCreditChoice:
          isInstantTaxCreditSelected || user?.wecasa_care_pairing_status === 'successful' ? 'Instant' : 'Classic',
        universe,
        user,
        utmMedium,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isUserReady, mode, user, universe, isInstantTaxCreditSelected]);

  const basketContextValue = useMemo(() => ({ show: showBasket }), [showBasket]);

  const onLoginButtonClick = useCallback(() => {
    if (isLoginOrSignup(location.pathname)) {
      trackSignInEvent({
        eventType: 'LogInButtonClick',
        path: location.pathname,
        universe,
      });
    }
    openLoginScreen();
  }, [universe, location.pathname, openLoginScreen]);

  return (
    <BasketContext.Provider value={basketContextValue}>
      <Header authenticationStatus={status} openLoginScreen={onLoginButtonClick} user={user} universe={universe} />
      <Container>
        <Row>
          <Main $centered={!showBasket}>
            <Routes>
              <Route
                path={`${route('root')}*`}
                element={
                  <FormContainer
                    universe={universe}
                    isSignedIn={status === 'authenticated'}
                    isLeavingApp={isLeavingApp}
                  />
                }
              />
            </Routes>
          </Main>
          <DesktopAndTablet>
            {showBasket && (
              <BasketContainer>
                <Basket pathname={location.pathname} shouldShowPromoCode={location.pathname === route('recap')} />
              </BasketContainer>
            )}
          </DesktopAndTablet>
        </Row>
      </Container>
    </BasketContext.Provider>
  );
};

export default MainContainer;
